import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { Vector3, Color3, Color4 } from "@babylonjs/core/Maths/math";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import "@babylonjs/core/Materials/standardMaterial";
import "@babylonjs/core/Meshes/Builders/groundBuilder";

/**
 * GENESIS ENGINE — Babylon.js bootstrap: engine, scene, camera, lights, render
 * loop, fullscreen and resize. One instance per running game scene.
 */
export class GenesisEngine {
  readonly engine: Engine;
  readonly scene: Scene;
  readonly camera: ArcRotateCamera;
  /** Soft shadows for depth realism — WorldManager registers casters. */
  shadows: ShadowGenerator | null = null;
  private readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true, antialias: true });
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.55, 0.72, 0.83, 1);

    // Orbit camera the user can rotate/zoom (mouse + touch). lockedTarget follows the player.
    this.camera = new ArcRotateCamera("cam", -Math.PI / 2, Math.PI / 3.1, 26, new Vector3(0, 1, 0), this.scene);
    this.camera.lowerBetaLimit = 0.25;
    this.camera.upperBetaLimit = Math.PI / 2.15;
    this.camera.lowerRadiusLimit = 8;
    this.camera.upperRadiusLimit = 60;
    this.camera.wheelDeltaPercentage = 0.02;
    this.camera.panningSensibility = 0; // disable pan; movement is via the avatar
    this.camera.attachControl(canvas, true);

    const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 0.85;
    hemi.groundColor = new Color3(0.35, 0.35, 0.4);

    const sun = new DirectionalLight("sun", new Vector3(-0.5, -1, -0.4), this.scene);
    sun.position = new Vector3(20, 40, 20);
    sun.intensity = 1.1;

    // Depth realism: soft shadows + distance fog (tinted per-scene in applyLighting).
    this.shadows = new ShadowGenerator(1024, sun);
    this.shadows.useBlurExponentialShadowMap = true;
    this.shadows.blurKernel = 16;
    this.scene.fogMode = Scene.FOGMODE_LINEAR;
    this.scene.fogStart = 40;
    this.scene.fogEnd = 120;

    window.addEventListener("resize", this.onResize);
  }

  applyLighting(kind: "day" | "dusk" | "night" | "glory", skyHex: string): void {
    const sky = Color3.FromHexString(skyHex);
    this.scene.fogColor = sky; // fog blends into the sky for depth
    const hemi = this.scene.getLightByName("hemi") as HemisphericLight | null;
    const sun = this.scene.getLightByName("sun") as DirectionalLight | null;
    switch (kind) {
      case "night":
        this.scene.clearColor = new Color4(0.05, 0.07, 0.13, 1);
        if (hemi) hemi.intensity = 0.35;
        if (sun) sun.intensity = 0.4;
        break;
      case "dusk":
        this.scene.clearColor = new Color4(sky.r * 0.7, sky.g * 0.55, sky.b * 0.6, 1);
        if (hemi) hemi.intensity = 0.6;
        if (sun) sun.intensity = 0.8;
        break;
      case "glory":
        this.scene.clearColor = new Color4(0.96, 0.94, 0.8, 1);
        if (hemi) hemi.intensity = 1.15;
        if (sun) sun.intensity = 1.4;
        break;
      default: // day
        this.scene.clearColor = new Color4(sky.r, sky.g, sky.b, 1);
        if (hemi) hemi.intensity = 0.9;
        if (sun) sun.intensity = 1.1;
    }
  }

  start(): void {
    this.engine.runRenderLoop(() => this.scene.render());
  }

  toggleFullscreen(): void {
    const el = this.canvas.parentElement ?? this.canvas;
    if (!document.fullscreenElement) {
      void el.requestFullscreen?.().catch(() => this.engine.enterFullscreen(false));
    } else {
      void document.exitFullscreen?.();
    }
  }

  private onResize = () => this.engine.resize();

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    this.scene.dispose();
    this.engine.dispose();
  }
}
